// src/main/MainPage.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, Link as RouterLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
    MessageSquare, Edit3, Zap, Lightbulb, Settings as SettingsIconLucide, HelpCircle,
    Bot as BotIconLucide, Menu, X as CloseIcon, Users, ClipboardList, CalendarDays, LogOut, User as UserIcon, Shield, LogIn, Edit, ShieldCheck as SpecialistPanelIcon
} from 'lucide-react';
import {
    AppContainer, Sidebar, SidebarHeader, Nav, NavButtonStyled, SidebarFooter,
    MainContent, MobileMenuButton, MobileOverlay, UserInfoWrapper
} from './LayoutStyles';
import { Logo as CommonLogo, StyledButton as CommonStyledButton } from './CommonStyles';
import { useAuth } from '../auth/AuthContext';
import { useFeatures } from '../feature/FeatureContext';
import { useTheme } from 'styled-components';
import apiClient from '../services/api';

// Importy wszystkich komponentów stron
import Dashboard from '../dashboard/Dashboard';
import ChatPage from '../chat/Chat';
import ReportForm from '../report/ReportForm';
import MyReportsPage from '../report/MyReportsPage';
import ReportDetailView from '../report/ReportDetailView';
import NotebookPage from '../journal/Notebook';
import ExercisesPage from '../exercise/Exercises';
import KnowledgePage from '../article/Knowledge';
import FullArticleView from '../article/FullArticleView';
import PersonalityTestsPage from '../test/PersonalityTests';
import PsychologistListPage from "../profile/PsychologistList";
import PublicProfileView from "../profile/PublicProfileView";
import AppointmentBookingPage from "../appointment/AppointmentBooking";
import MyAppointments from '../appointment/MyAppointments';
import SettingsPage from '../settings/Settings';
import HelpPage from '../help/Help';
import ArticleEditor from '../article/ArticleEditor';
import PsychologistProfileEditor from '../profile/PsychologistProfileEditor';
import SpecialistPanel from '../specialist/SpecialistPanel';
import AdminDashboard from '../admin/AdminDashboard';
import SystemAdminDashboard from '../admin/SystemAdminDashboard';
import ProtectedRoute from '../utils/ProtectedRoute';
import RoleBasedRoute from '../utils/RoleBasedRoute';
import TermsOfServicePage from '../terms/TermsOfService';
import ClaimAccountPage from '../auth/ClaimAccountPage';

// NOWY IMPORT
import BottomNavBar from './BottomNavBar';

const ALL_POSSIBLE_NAV_ITEMS = [
    { name: 'Pulpit', path: 'dashboard', icon: BotIconLucide, alwaysVisible: true },
    { name: 'Czat AI', path: 'chat', icon: MessageSquare, featureKey: 'AI_CHAT' },
    { name: 'Zgłoś Sprawę', path: 'report-form', icon: MessageSquare, requiresLogin: true, featureKey: 'REPORTING' },
    { name: 'Moje Sprawy', path: 'my-reports', icon: ClipboardList, requiresLogin: true, featureKey: 'REPORTING' },
    { name: 'Dziennik', path: 'journal', icon: Edit3, requiresLogin: true, featureKey: 'JOURNAL' },
    { name: 'Ćwiczenia', path: 'exercises', icon: Zap, featureKey: 'EXERCISES' },
    { name: 'Baza Wiedzy', path: 'knowledge', icon: Lightbulb, featureKey: 'KNOWLEDGE_BASE' },
    { name: 'Testy', path: 'tests', icon: ClipboardList, featureKey: 'TESTS' },
    { name: 'Specjaliści', path: 'psychologists', icon: Users, featureKey: 'PSYCHOLOGIST_LIST' },
    { name: 'Wizyty', path: 'appointments', icon: CalendarDays, requiresLogin: true, featureKey: 'APPOINTMENTS' },
    { name: 'Moje Wizyty', path: 'my-appointments', icon: CalendarDays, requiresLogin: true, featureKey: 'APPOINTMENTS' },
    { name: 'Panel Specjalisty', path: 'specialist-panel/*', icon: SpecialistPanelIcon, requiresLogin: true, forRole: 'isPsychologist', featureKey: 'SPECIALIST_PANEL' },
    { name: 'Panel Admina', path: 'admin-panel/*', icon: Shield, requiresLogin: true, forRole: 'isAdmin', featureKey: 'ADMIN_PANEL' },
    { name: 'Panel Systemu', path: 'system-admin-panel/*', icon: Shield, requiresLogin: true, forRole: 'isSystemAdmin', featureKey: 'SYSTEM_ADMIN_PANEL' },
    { name: 'Pomoc', path: 'help', icon: HelpCircle, featureKey: 'HELP' },
    { name: 'Ustawienia', path: 'settings', icon: SettingsIconLucide, requiresLogin: true, alwaysVisible: true },
];

const MainPage = () => {
    const theme = useTheme();
    const { currentUser, logout, isAdmin, isSystemAdmin, isPsychologist } = useAuth();
    const { features, isLoading: isFeaturesLoading } = useFeatures();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const showClaimAccountBanner = currentUser && !currentUser.passwordIsSet;

    // NOWY STAN DLA KONFIGURACJI PASKA
    const [bottomNavConfig, setBottomNavConfig] = useState([]);

    const navigationItems = useMemo(() => {
        const userRoles = { isAdmin, isSystemAdmin, isPsychologist };

        return ALL_POSSIBLE_NAV_ITEMS.filter(item => {
            if (item.requiresLogin && !currentUser) {
                return false;
            }
            if (item.alwaysVisible) {
                return !!currentUser;
            }
            if (item.featureKey && !features[item.featureKey]) {
                return false;
            }
            if (item.forRole && !userRoles[item.forRole]) {
                return false;
            }
            if (item.name === 'Moje Wizyty' && features['APPOINTMENTS']) return false;
            if (item.name === 'Zgłoś Sprawę' && features['REPORTING']) return false;

            return true;
        });
    }, [features, currentUser, isAdmin, isSystemAdmin, isPsychologist]);


    const getActivePageName = useCallback(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        if (pathSegments.length < 2 || pathSegments[0] !== 'app') return 'Pulpit';
        const pagePath = pathSegments[1];
        const foundItem = ALL_POSSIBLE_NAV_ITEMS.find(item => pagePath.startsWith(item.path.replace('/*', '')));
        return foundItem ? foundItem.name : 'Pulpit';
    }, [location.pathname]);

    // NOWY EFEKT DO POBIERANIA KONFIGURACJI PASKA
    useEffect(() => {
        const fetchBottomNavConfig = async () => {
            try {
                const response = await apiClient.get('/features/bottom-nav-config');
                setBottomNavConfig(response.data);
            } catch (error) {
                console.error("Nie udało się pobrać konfiguracji paska nawigacyjnego:", error);
            }
        };
        fetchBottomNavConfig();
    }, []);

    const activePage = getActivePageName();
    const handleSidebarToggle = () => setIsSidebarOpen(!isSidebarOpen);
    const handleOverlayClick = () => setIsSidebarOpen(false);

    useEffect(() => {
        document.body.style.overflow = isSidebarOpen ? 'hidden' : 'auto';
        return () => { document.body.style.overflow = 'auto'; };
    }, [isSidebarOpen]);

    const handleNavButtonClick = (path, requiresLogin) => {
        if (requiresLogin && !currentUser) {
            navigate('/login', { state: { from: location, message: `Aby uzyskać dostęp, musisz się zalogować.` } });
        } else {
            navigate(`/app/${path.replace('/*', '')}`);
        }
        if (isSidebarOpen) setIsSidebarOpen(false);
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    if (isFeaturesLoading) {
        return <div style={{textAlign: 'center', padding: '50px'}}>Ładowanie funkcji...</div>;
    }

    return (
        <AppContainer>
            <MobileMenuButton onClick={handleSidebarToggle}>
                {isSidebarOpen ? <CloseIcon size={22} /> : <Menu size={22} />}
            </MobileMenuButton>
            <MobileOverlay isOpen={isSidebarOpen} onClick={handleOverlayClick} />

            <Sidebar isOpen={isSidebarOpen}>
                <SidebarHeader>
                    <RouterLink to="/app/dashboard" style={{ textDecoration: 'none' }}>
                        <CommonLogo>
                            <BotIconLucide size={30} />
                            <span>ePsycholog</span>
                        </CommonLogo>
                    </RouterLink>
                    <p className="app-subtitle">Twój kompas w świecie emocji</p>
                </SidebarHeader>

                <UserInfoWrapper>
                    {currentUser ? (
                        <>
                            {currentUser.imageUrl ? (
                                <img src={currentUser.imageUrl} alt={currentUser.username} className="user-avatar" />
                            ) : (
                                <UserIcon size={36} className="user-avatar" style={{border: 'none', padding: '2px', color: '#94a3b8'}}/>
                            )}
                            <div className="user-details">
                                <div className="username">{currentUser.username}</div>
                                <div className="user-email">{currentUser.email}</div>
                                {isPsychologist && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', marginTop: '4px' }}>
                                        <RouterLink
                                            to="/app/profile/edit"
                                            style={{ fontSize: '0.75rem', fontWeight: '600', color: theme.colors.primaryDark, display: 'flex', alignItems: 'center', gap: '4px' }}
                                            onClick={() => isSidebarOpen && setIsSidebarOpen(false)}
                                        >
                                            <Edit size={14} /> Edytuj Wizytówkę
                                        </RouterLink>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="guest-actions">
                            <p>Korzystasz jako gość.</p>
                            <CommonStyledButton onClick={() => navigate('/login')}><LogIn size={16} /> Zaloguj się</CommonStyledButton>
                            <p style={{marginTop: '0.3rem'}}>lub <RouterLink to="/register">zarejestruj się</RouterLink></p>
                        </div>
                    )}
                </UserInfoWrapper>

                <Nav className="custom-scrollbar">
                    {navigationItems.map((item) => (
                        <NavButtonStyled
                            key={item.name}
                            $active={activePage === item.name}
                            onClick={() => handleNavButtonClick(item.path, item.requiresLogin)}
                            title={item.name + ((item.requiresLogin && !currentUser) ? " (wymaga zalogowania)" : "")}>
                            <item.icon size={20} />
                            <span>{item.name}</span>
                            {item.requiresLogin && !currentUser && <LogIn size={14} style={{ opacity: 0.6, marginLeft: 'auto' }} />}
                        </NavButtonStyled>
                    ))}
                </Nav>

                <SidebarFooter>
                    {currentUser && <NavButtonStyled onClick={handleLogout} $active={false}><LogOut size={20} /><span>Wyloguj</span></NavButtonStyled>}
                    <p className="copyright-text">© {new Date().getFullYear()} ePsycholog.</p>
                </SidebarFooter>
            </Sidebar>

            <MainContent id="main-content-area">
                {showClaimAccountBanner && (
                    <div style={{ padding: '1rem', backgroundColor: '#fffbe6', borderBottom: '1px solid #fde68a', position: 'sticky', top: 0, zIndex: 100 }}>
                        <p style={{ textAlign: 'center', margin: 0, fontWeight: 500 }}>
                            <strong style={{color: '#f59e0b'}}>Zabezpiecz swoje konto!</strong>{' '}
                            <RouterLink to="/app/claim-account" style={{ textDecoration: 'underline', fontWeight: '700' }}>
                                Ustaw hasło, aby nie utracić dostępu.
                            </RouterLink>
                        </p>
                    </div>
                )}
                <Routes>
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                    <Route path="claim-account" element={<ProtectedRoute><ClaimAccountPage/></ProtectedRoute>} />
                    {features['AI_CHAT'] && <Route path="chat" element={<ChatPage />} />}
                    {features['REPORTING'] && (
                        <>
                            <Route path="report-form" element={<ProtectedRoute><ReportForm /></ProtectedRoute>} />
                            <Route path="my-reports" element={<ProtectedRoute><MyReportsPage /></ProtectedRoute>} />
                            <Route path="report/:id" element={<ProtectedRoute><ReportDetailView /></ProtectedRoute>} />
                        </>
                    )}
                    {features['JOURNAL'] && <Route path="journal" element={<ProtectedRoute><NotebookPage /></ProtectedRoute>} />}
                    {features['EXERCISES'] && <Route path="exercises" element={<ExercisesPage />} />}
                    {features['KNOWLEDGE_BASE'] && (
                        <>
                            <Route path="knowledge" element={<KnowledgePage />} />
                            <Route path="knowledge/:slug" element={<FullArticleView />} />
                        </>
                    )}
                    {features['TESTS'] && <Route path="tests" element={<PersonalityTestsPage />} />}
                    {features['APPOINTMENTS'] && (
                        <>
                            <Route path="appointments" element={<ProtectedRoute><AppointmentBookingPage /></ProtectedRoute>} />
                            <Route path="my-appointments" element={<ProtectedRoute><MyAppointments /></ProtectedRoute>} />
                        </>
                    )}
                    {features['PSYCHOLOGIST_LIST'] && (
                        <>
                            <Route path="psychologists" element={<PsychologistListPage />} />
                            <Route path="psychologist/:id" element={<PublicProfileView />} />
                        </>
                    )}
                    {features['HELP'] && <Route path="help" element={<HelpPage />} />}
                    {features['ARTICLE_CREATION'] && <Route path="create-article" element={<RoleBasedRoute rolesAllowed={['ROLE_PSYCHOLOGIST', 'ROLE_ADMIN', 'ROLE_SYSTEM_ADMIN']}><ArticleEditor /></RoleBasedRoute>} />}
                    {features['SPECIALIST_PANEL'] && <Route path="specialist-panel/*" element={<RoleBasedRoute rolesAllowed={['ROLE_PSYCHOLOGIST']}><SpecialistPanel /></RoleBasedRoute>} />}
                    {features['ADMIN_PANEL'] && <Route path="admin-panel/*" element={<RoleBasedRoute rolesAllowed={['ROLE_ADMIN', 'ROLE_SYSTEM_ADMIN']}><AdminDashboard /></RoleBasedRoute>} />}
                    {features['SYSTEM_ADMIN_PANEL'] && <Route path="system-admin-panel/*" element={<RoleBasedRoute rolesAllowed={['ROLE_SYSTEM_ADMIN']}><SystemAdminDashboard /></RoleBasedRoute>} />}
                    <Route path="terms-of-service" element={<TermsOfServicePage />} />
                    <Route path="profile/edit" element={<RoleBasedRoute rolesAllowed={['ROLE_PSYCHOLOGIST']}><PsychologistProfileEditor /></RoleBasedRoute>} />
                    <Route path="*" element={<Navigate to="dashboard" replace />} />
                </Routes>
            </MainContent>

            {/* DODANY KOMPONENT PASKA NAWIGACYJNEGO */}
            <BottomNavBar config={bottomNavConfig} />
        </AppContainer>
    );
};

export default MainPage;